import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Volume2, RotateCcw, User, MessageCircle } from 'lucide-react';
import { resolveMediaUrl } from '../../../utils/mediaUrl';
import { useTranslation } from '../../../i18n';
import useSyncedState from './useSyncedState';
import ReferenceScale from './ReferenceScale';

// Діалог-симулятор — a chat with a character. Each node is one bot line plus
// reply options for the student. A wrong option turns red (and the bot may
// react) without moving on; a correct option goes to `nextId` (empty = next
// node in the list, '__end' = finish), which is how branching works.
// Synced state: { nodeId, history: [{ from: 'bot'|'me', text, wrong? }], wrongIds, done, rev }.

export const DIALOGUE_END = '__end';
const TYPING_MS = 650;

const speak = (text, lang) => {
  if (!text || typeof window === 'undefined' || !window.speechSynthesis) return;
  try {
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = lang || 'en-US';
    const voices = window.speechSynthesis.getVoices();
    const match = voices.find((v) => v.lang === utter.lang) || voices.find((v) => v.lang?.startsWith(utter.lang.split('-')[0]));
    if (match) utter.voice = match;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utter);
  } catch (_) {}
};

const startState = (nodes) => {
  const first = nodes[0];
  return {
    nodeId: first ? first.id : null,
    history: first && first.botText ? [{ from: 'bot', text: first.botText }] : [],
    wrongIds: [],
    done: !first,
  };
};

const resolveNext = (nodes, node, option) => {
  if (option.nextId === DIALOGUE_END) return null;
  if (option.nextId) return nodes.find((n) => n.id === option.nextId) || null;
  const idx = nodes.findIndex((n) => n.id === node.id);
  return nodes[idx + 1] || null;
};

const DialogueInner = ({
  element,
  isEditor = false,
  canInteract = true,
  syncedState = null,
  onStateChange,
}) => {
  const { t } = useTranslation();
  const {
    title = '',
    characterName = '',
    avatarUrl = null,
    nodes = [],
    autoSpeak = false,
    voiceLang = 'en-US',
    botBubbleColor = '#F3EFEA',
    botTextColor = '#1f2937',
    myBubbleColor = '#2A4D25',
    myTextColor = '#ffffff',
    optionBgColor = '#ffffff',
    textColor = '#1f2937',
    fontFamily = 'Evolventa',
    fontSize = 16,
  } = element.content || {};

  const nodesKey = useMemo(() => nodes.map((n) => n.id).join('|'), [nodes]);
  const [state, update] = useSyncedState(syncedState, onStateChange, () => startState(nodes));
  const interactive = !isEditor && canInteract;

  // If the first node changed in the editor/preview, restart the local run.
  useEffect(() => {
    if (isEditor) return;
    if (state.nodeId && !nodes.some((n) => n.id === state.nodeId) && !state.done) {
      update(() => startState(nodes));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodesKey]);

  const history = useMemo(
    () => (isEditor ? startState(nodes).history : (state.history || [])),
    [isEditor, nodes, state.history],
  );
  const current = nodes.find((n) => n.id === state.nodeId) || (isEditor ? nodes[0] : null);
  const wrongIds = state.wrongIds || [];

  // Reveal new bot messages one at a time with a short "typing…" pause.
  const [revealed, setRevealed] = useState(history.length);
  useEffect(() => {
    if (revealed > history.length) { setRevealed(history.length); return undefined; }
    if (revealed === history.length) return undefined;
    const nextMsg = history[revealed];
    const id = setTimeout(() => setRevealed((r) => r + 1), nextMsg.from === 'bot' ? TYPING_MS : 120);
    return () => clearTimeout(id);
  }, [revealed, history]);
  const visible = history.slice(0, revealed);
  const typing = revealed < history.length && history[revealed].from === 'bot';
  const settled = revealed >= history.length;

  // Auto-speak only on the answering device to avoid double audio.
  const lastSpokenRef = useRef(-1);
  useEffect(() => {
    if (!autoSpeak || !interactive) return;
    const idx = visible.length - 1;
    if (idx < 0 || idx <= lastSpokenRef.current) return;
    lastSpokenRef.current = idx;
    if (visible[idx].from === 'bot') speak(visible[idx].text, voiceLang);
  }, [visible.length, autoSpeak, interactive, voiceLang]); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollRef = useRef(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [visible.length, typing]);

  const choose = (option) => {
    if (!interactive || !current || state.done || !settled) return;
    if (wrongIds.includes(option.id)) return;
    update((prev) => {
      const hist = [...(prev.history || []), { from: 'me', text: option.text, wrong: option.correct === false }];
      if (option.correct === false) {
        if (option.reply) hist.push({ from: 'bot', text: option.reply });
        return { ...prev, history: hist, wrongIds: [...(prev.wrongIds || []), option.id] };
      }
      if (option.reply) hist.push({ from: 'bot', text: option.reply });
      const next = resolveNext(nodes, current, option);
      if (next && next.botText) hist.push({ from: 'bot', text: next.botText });
      return { ...prev, history: hist, wrongIds: [], nodeId: next ? next.id : null, done: !next };
    });
  };

  const restart = () => {
    lastSpokenRef.current = -1;
    setRevealed(0);
    update(() => startState(nodes));
  };

  const fs = fontSize;
  const avatarSize = Math.round(fs * 2.2);
  const options = current && !state.done ? (current.options || []).filter((o) => (o.text || '').trim()) : [];

  const avatar = (
    <div className="shrink-0 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center" style={{ width: avatarSize, height: avatarSize }}>
      {avatarUrl
        ? <img src={resolveMediaUrl(avatarUrl) || ''} alt="" draggable={false} className="w-full h-full object-cover" />
        : <User size={avatarSize * 0.55} className="text-gray-400" />}
    </div>
  );

  return (
    <div className="w-full h-full flex flex-col overflow-hidden" style={{ fontFamily, color: textColor }}>
      <div className="shrink-0 flex items-center gap-2.5 border-b border-black/5" style={{ padding: '8px 12px' }}>
        {avatar}
        <div className="min-w-0 flex-1">
          <div className="font-semibold leading-tight truncate" style={{ fontSize: fs }}>{characterName || title || 'Chat'}</div>
          {title && characterName && <div className="opacity-60 truncate" style={{ fontSize: fs * 0.75 }}>{title}</div>}
        </div>
        {interactive && history.length > 1 && (
          <button
            type="button"
            onClick={restart}
            title={t('games.dialogue.restart', { defaultValue: 'Почати діалог спочатку' })}
            className="p-1.5 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100"
          >
            <RotateCcw size={15} />
          </button>
        )}
      </div>

      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto flex flex-col" style={{ padding: 12, gap: 8 }}>
        {nodes.length === 0 && (
          <div className="m-auto flex flex-col items-center text-gray-300">
            <MessageCircle size={40} />
          </div>
        )}
        {visible.map((msg, i) => (msg.from === 'bot' ? (
          <div key={i} className="flex items-end gap-2 max-w-[85%]">
            <div
              className="rounded-2xl rounded-bl-md leading-snug break-words"
              style={{ backgroundColor: botBubbleColor, color: botTextColor, fontSize: fs, padding: '8px 12px' }}
            >
              {msg.text}
            </div>
            {!isEditor && (
              <button
                type="button"
                onClick={() => speak(msg.text, voiceLang)}
                className="shrink-0 p-1 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100"
              >
                <Volume2 size={14} />
              </button>
            )}
          </div>
        ) : (
          <div key={i} className="self-end max-w-[85%]">
            <div
              className="rounded-2xl rounded-br-md leading-snug break-words"
              style={{
                backgroundColor: msg.wrong ? '#fee2e2' : myBubbleColor,
                color: msg.wrong ? '#991b1b' : myTextColor,
                border: msg.wrong ? '1.5px solid #fca5a5' : 'none',
                textDecoration: msg.wrong ? 'line-through' : 'none',
                fontSize: fs,
                padding: '8px 12px',
              }}
            >
              {msg.text}
            </div>
          </div>
        )))}
        {typing && (
          <div className="flex items-center gap-1 rounded-2xl rounded-bl-md self-start" style={{ backgroundColor: botBubbleColor, padding: '10px 14px' }}>
            {[0, 1, 2].map((d) => (
              <span key={d} className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: `${d * 0.15}s` }} />
            ))}
          </div>
        )}
        {!isEditor && state.done && settled && (
          <div className="self-center text-center opacity-50 mt-1" style={{ fontSize: fs * 0.8 }}>
            {t('games.dialogue.end', { defaultValue: '— кінець діалогу —' })}
          </div>
        )}
      </div>

      {options.length > 0 && (
        <div className="shrink-0 border-t border-black/5 flex flex-wrap justify-end" style={{ padding: 10, gap: 6 }}>
          {options.map((o) => {
            const wrong = wrongIds.includes(o.id);
            const enabled = interactive && settled && !wrong;
            return (
              <button
                key={o.id}
                type="button"
                onClick={() => choose(o)}
                disabled={!enabled}
                className="rounded-full text-left leading-snug transition"
                style={{
                  fontSize: fs * 0.95,
                  padding: '6px 14px',
                  backgroundColor: wrong ? '#fee2e2' : optionBgColor,
                  color: wrong ? '#991b1b' : textColor,
                  border: `1.5px solid ${wrong ? '#fca5a5' : myBubbleColor}`,
                  opacity: wrong ? 0.6 : (settled || isEditor ? 1 : 0.5),
                  cursor: enabled ? 'pointer' : 'default',
                }}
              >
                {o.text}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const DialogueElement = ({ slideScale = 1, ...props }) => (
  <ReferenceScale scale={slideScale}>
    <DialogueInner {...props} />
  </ReferenceScale>
);

export default DialogueElement;
